import {
  VEHICLE_PANEL_KEYS,
  getPanelOperationLabel,
  getPartOriginLabel,
  getVehiclePanelLabel,
  isPanelOperation,
  isVehiclePanel,
  panelItemDescription,
  type VehiclePanel,
} from '@mustauto/types/vehicle-panels';

/**
 * Orçamento por peça (F3.3 dos módulos verticais) — a regra de agrupamento que
 * TODA tela de itens usa: app do mecânico, app do gestor, orçamento do cliente
 * e o painel web. Uma regra só, senão o cliente e a oficina leriam o mesmo
 * orçamento arrumado de dois jeitos.
 *
 * O item continua sendo `part`/`labor`/`outsourced` para total e estoque (D4);
 * a peça do carro (`panel`) é o que agrupa. Quem decide mostrar agrupado é a
 * presença de peça nos itens, não o módulo da OS: OS de mecânica nunca tem
 * peça, e o mesmo código serve à estética quando ela usar peça.
 *
 * Importa o subcaminho `@mustauto/types/vehicle-panels`, não o índice: o
 * índice puxa o `zod`, que a imagem do site não instala (ver CLAUDE.md, #154).
 */

/** Os campos que o agrupamento lê. Qualquer item de qualquer superfície serve. */
export interface PanelItemFields {
  type?: unknown;
  description?: unknown;
  panel?: unknown;
  operation?: unknown;
  partOrigin?: unknown;
}

export interface PanelItemGroup<T> {
  panel: VehiclePanel;
  /** Nome da peça do carro — o título do bloco. */
  label: string;
  items: T[];
}

export interface ItemsByPanel<T> {
  /** Um bloco por peça, na ordem da taxonomia (frente para trás). */
  panels: PanelItemGroup<T>[];
  /** O que não é de uma peça: alinhamento, material de pintura, e item de peça desconhecida. */
  others: T[];
}

/** A lista tem item de alguma peça do carro? É o que liga a leitura agrupada. */
export function hasPanelItems(items: readonly PanelItemFields[] | null | undefined): boolean {
  return (items ?? []).some((item) => isVehiclePanel(item.panel));
}

/**
 * Agrupa por peça, mantendo a ordem original dentro de cada bloco. Peça fora
 * da taxonomia (gravada por uma versão que conhecia outra lista) cai em
 * "outros" — o item aparece, só não ganha bloco com um nome que não existe.
 */
export function groupItemsByPanel<T extends PanelItemFields>(items: readonly T[]): ItemsByPanel<T> {
  const byPanel = new Map<VehiclePanel, T[]>();
  const others: T[] = [];
  for (const item of items) {
    if (isVehiclePanel(item.panel)) {
      const list = byPanel.get(item.panel);
      if (list) list.push(item);
      else byPanel.set(item.panel, [item]);
    } else {
      others.push(item);
    }
  }
  const panels = VEHICLE_PANEL_KEYS.filter((panel) => byPanel.has(panel)).map((panel) => ({
    panel,
    label: getVehiclePanelLabel(panel) ?? panel,
    items: byPanel.get(panel)!,
  }));
  return { panels, others };
}

/** Um bloco da lista agrupada: uma peça do carro, ou "Outros itens" (`panel: null`). */
export interface QuoteItemGroup<T> {
  key: string;
  panel: VehiclePanel | null;
  label: string;
  items: T[];
  /** Soma do bloco, na unidade que `totalOf` devolve (centavos ou reais). */
  subtotal: number;
}

export const OTHER_ITEMS_LABEL = 'Outros itens';

/**
 * A lista inteira, pronta para desenhar: um bloco por peça, na ordem do carro,
 * e "Outros itens" no fim quando houver — cada um com o subtotal. É ISTO que
 * as telas usam, e não `groupItemsByPanel` + montagem própria: cada tela que
 * montava a sua decidia sozinha o que "outros" mostra, e elas divergiam.
 *
 * `totalOf` porque cada superfície guarda o total de um jeito (o app em
 * centavos no `total`, o app do gestor só com quantidade e preço).
 */
export function panelItemGroups<T extends PanelItemFields>(
  items: readonly T[],
  totalOf: (item: T) => number,
): QuoteItemGroup<T>[] {
  const { panels, others } = groupItemsByPanel(items);
  const sum = (list: T[]) => list.reduce((acc, item) => acc + totalOf(item), 0);
  return [
    ...panels.map((g) => ({ key: g.panel, panel: g.panel, label: g.label, items: g.items, subtotal: sum(g.items) })),
    ...(others.length > 0
      ? [{ key: 'others', panel: null, label: OTHER_ITEMS_LABEL, items: others, subtotal: sum(others) }]
      : []),
  ];
}

/**
 * O texto da linha DENTRO do bloco da peça. O item que a avaliação de dano
 * criou diz "Reparar — Porta dianteira esquerda"; debaixo do título "Porta
 * dianteira esquerda", repetir a peça é ruído, e a linha vira só "Reparar".
 * Descrição que o mecânico editou fica como ele escreveu.
 */
export function panelItemLabel(item: PanelItemFields): string {
  const description = typeof item.description === 'string' ? item.description : '';
  if (
    isVehiclePanel(item.panel) &&
    isPanelOperation(item.operation) &&
    description === panelItemDescription(item.panel, item.operation)
  ) {
    return getPanelOperationLabel(item.operation);
  }
  return description;
}

/**
 * Procedência de uma peça, para a linha: "Genuína", ou "Procedência não
 * informada" quando o mecânico ainda não escolheu — opcional, mas visível (o
 * que falta fica à vista).
 *
 * "Não informada" só na peça que é de uma PEÇA DO CARRO: material de pintura
 * e lixa, em "Outros itens", não têm procedência a informar, e o cliente leria
 * a falta como descuido. Procedência escolhida aparece sempre. `null` para quem
 * não é peça, e para a peça solta sem procedência.
 */
export function partOriginText(item: PanelItemFields): string | null {
  if (item.type !== 'part') return null;
  const label = getPartOriginLabel(item.partOrigin);
  if (label) return label;
  return isVehiclePanel(item.panel) ? 'Procedência não informada' : null;
}

/**
 * O tipo do item na linha ("Peça", "Mão de obra", "Terceirizado"). Mora aqui
 * porque no orçamento por peça é a linha que diz o tipo — o título do bloco é
 * a peça do carro —, e a página pública, o e-mail e os dois PDFs tinham cada um
 * a sua tabela. Tipo desconhecido vira "Item", nunca a chave crua.
 */
export const ITEM_TYPE_LABELS: Readonly<Record<string, string>> = {
  part: 'Peça',
  labor: 'Mão de obra',
  outsourced: 'Terceirizado',
};

export function itemTypeLabel(type: unknown): string {
  return (typeof type === 'string' && ITEM_TYPE_LABELS[type]) || 'Item';
}
